/**
 * Car Racing game-domain types.
 * Ported from packages/amgcarracing_v2/lib/core/constants/game_constants.dart
 * (CarRacingBetAreas, CarRacingResultMappings).
 *
 * Eight bet areas: a Gold (G*) and Silver (S*) variant of each of four cars.
 * The numeric Rive `Spin` value is keyed off the winning betItemId via
 * SPIN_MAPPING in config.ts.
 */

export type GamePhase =
  | 'idle'
  | 'initializing'
  | 'modeSelection'
  | 'betting'
  | 'bettingClosed'
  | 'waitingResult'
  | 'racing' // race animation (Spin) is playing
  | 'showResult'
  | 'result'
  | 'newRound'
  | 'error';

export type BetType =
  | 'GLamborghini'
  | 'GFerrari'
  | 'GBenz'
  | 'GBmw'
  | 'SLamborghini'
  | 'SFerrari'
  | 'SBenz'
  | 'SBmw';

/** All bet types in display order (matches CarRacingBetAreas.names). */
export const ALL_BETS: readonly BetType[] = [
  'GLamborghini',
  'GFerrari',
  'GBenz',
  'GBmw',
  'SLamborghini',
  'SFerrari',
  'SBenz',
  'SBmw',
];

/** Position-matched to ALL_BETS — index i ↔ betItemId at ID[i]. */
export const BET_ITEM_IDS: readonly number[] = [
  377, // GLamborghini
  376, // GFerrari
  375, // GBenz
  374, // GBmw
  373, // SLamborghini
  372, // SFerrari
  371, // SBenz
  370, // SBmw
];

export const BET_ITEM_ID: Record<BetType, number> = {
  GLamborghini: 377,
  GFerrari: 376,
  GBenz: 375,
  GBmw: 374,
  SLamborghini: 373,
  SFerrari: 372,
  SBenz: 371,
  SBmw: 370,
};

/** Reverse lookup. */
export function betTypeFromId(id: number): BetType | null {
  const idx = BET_ITEM_IDS.indexOf(id);
  return idx >= 0 ? ALL_BETS[idx] : null;
}

/**
 * Rive `BetButton` event property `betID` is 1-based (1..8) — it indexes
 * directly into ALL_BETS / BET_ITEM_IDS.
 */
export function betTypeFromRiveBetId(rive1Based: number): BetType | null {
  const idx = rive1Based - 1;
  if (idx < 0 || idx >= ALL_BETS.length) return null;
  return ALL_BETS[idx];
}

/** Human-readable label for a bet area. */
export function labelOf(b: BetType): string {
  switch (b) {
    case 'GLamborghini':
      return 'Gold Lamborghini';
    case 'GFerrari':
      return 'Gold Ferrari';
    case 'GBenz':
      return 'Gold Benz';
    case 'GBmw':
      return 'Gold BMW';
    case 'SLamborghini':
      return 'Silver Lamborghini';
    case 'SFerrari':
      return 'Silver Ferrari';
    case 'SBenz':
      return 'Silver Benz';
    case 'SBmw':
      return 'Silver BMW';
  }
}

export interface GameConfig {
  minBet: number;
  maxBet: number;
  chipValues: readonly number[];
  /**
   * Payout odds from the backend's GameOdd1, in server order (index 0..7).
   * Rendered into the Rive odds labels as txtOdd8..txtOdd1 (index 0 -> txtOdd8,
   * index 7 -> txtOdd1), e.g. "2.00,…,40.00" => txtOdd8 "x2" … txtOdd1 "x40".
   * Empty when the server hasn't sent odds yet.
   */
  gameOdds: readonly number[];
}

export interface GameRound {
  roundId: string;
  nextRoundId: string;
  /** Winning bet area for the round. */
  winner: BetType;
  /** Winning bet-item id (370..377). */
  winningBetItemId: number;
  totalWinLose: number;
  totalBet: number;
  positiveWinAmount: number;
  hasPositiveWin: boolean;
}
